/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useEffect, useState } from 'react';
import { Formik, Form, Field } from 'formik';
import root from 'window-or-global';
import Head from 'next/head';
import { useSelector } from 'react-redux';
import cx from 'classnames';
import { Grid } from '../Grid';
import { useLabels } from '@/hooks/useLabels';
import { useLocale } from '@/hooks/useLocale';
import FormikTextField from '../AZCustomComponent/FormGroup/TextField/formikTextField';
import { Button } from '@/components/Button';
import Title from '../AZCustomComponent/Title';
import ContentContainer from '../AZCustomComponent/ContentContainer';
import NotificationBlock from '../AZCustomComponent/NotificationBlock';
import { validateEmail } from '@/utils/validator/validateEmail';
import { validateEmailRequired } from '@/utils/validator/validateRequired/validateEmailRequired';
import { validateOrderNumberRequired } from '@/utils/validator/validateRequired/validateOrderNumberRequired';
import { SmartLink as Link } from '../../utils/smartLink';
import { routePaths } from '../../constants/routePaths';
import azCommonStyles from '../../theme/globals.module.scss';
import { useRouter } from 'next/router';
import type { ReduxState } from '../../types';
import styles from './styles.module.scss';
import { useAnalytics } from '@/utils/analytics/useAnalytics';
import { usePageViewCall } from '@/utils/analytics/usePageViewCall';
import { clickTrack } from '@/utils/analytics/clickTrack';
import { getPageName } from '@/utils/analytics/legacyUtils/getPageName';
import { pageTypes } from '@/utils/analytics/legacyUtils/pageTypes';
import { trackFormStartAction } from '@/utils/analytics/track/trackFormStartAction';
import { trackFormEndAction } from '@/utils/analytics/track/trackFormEndAction';
import { getSeoPageType } from '@/utils/analytics/legacyUtils/getSeoPageType';
import { getAnalyticsRelativeComplementOf } from '@/utils/analytics/getAnalyticsRelativeComplementOf';
import { countryCodes } from '@/constants/locale';
import { siteSections } from '@/utils/siteSections';
import { useTrackedOrder } from '@/features/trackOrder/api/getTrackedOrder';
import { getErrorCodeDescription } from '@/utils/errorsHandling';
import { type TrackedOrderOptions } from '@/features/trackOrder';
import { localStorage } from '@/utils/localStorage';
import ProgressModal from '../AZCustomComponent/ProgressModal';

const { siteSectionTrackOrder } = siteSections;

const trackOrderErrors = (errorMsg: string) => {
  clickTrack({
    eventType: 'errorTrackOrder',
    trackOrderErrorMessage: errorMsg,
  });
};

const trackSuccessfulTrackOrder = () => {
  clickTrack({
    eventType: 'successfulTrackOrder',
  });
};

const labelMap = {
  lblFindOrder: 'button_TrackMyOrder_TrackOrder_FindOrder',
  lblSignIn: 'button_TrackMyOrder_TrackOrder_SignIn',
  lblRegister: 'button_TrackMyOrder_TrackOrder_Register',
  lblHome: 'label_Footer_main_Home',
  lblTrackOrder: 'label_Footer_main_TrackMyOrder',
  lblTrack: 'label_TrackMyOrder_TrackOrder_Track',
  lblTrackMyOrder: 'label_TrackMyOrder_TrackOrder_MyOrder',
  lblFindASingleOrderWithTheFormBelow:
    'label_TrackMyOrder_TrackOrder_FindASingleOrderWithTheFormBelow',
  lblEmailAddress: 'label_CreateAccount_body_Email',
  lblOrderNumber: 'label_TrackMyOrder_OrderNumber',
  lblRegisterToStart:
    'label_TrackMyOrder_TrackOrder_SignInToSeeYourEntireOrderHistory?OrRegisterToStartTrackingOrders',
  lblProblem: 'label_TrackMyOrder_TrackOrder_Problem',
  lblContactSupport: 'label_TrackMyOrder_TrackOrder_ContactSupport',
  lblPleaseEnterAnEmailId: 'label_MyAccountMyProfile_editProfile_PleaseEnterAnEmailId',
  lblPleaseEnterAValidEmailId: 'label_MyAccountMyProfile_editProfile_PleaseEnterAValidEmailId',
  label_LandingPages_ShopByMakeModel_Home: 'label_LandingPages_ShopByMakeModel_Home',
  label_Sitemap_Links_TrackMyOrder: 'label_Sitemap_Links_TrackMyOrder',
  lblPageTitle: 'label_page_title_track_order',
};

export const TrackOrder = () => {
  const isUtagDefined = useAnalytics();
  const pageViewCall = usePageViewCall();
  const [findOrderClicked, setFindOrderClicked] = useState(false);
  const [errorMessagge, setErrMsg] = useState('');
  const labels = useLabels(labelMap);
  const router = useRouter();
  const locale = useLocale();

  const userAuthenticated = useSelector((state: ReduxState) => state.appData.userAuthenticated);

  const [trackOrderOptions, setTrackOrderOptions] = useState<TrackedOrderOptions>({});
  const [trackedOrderLoading, setTrackedOrderLoading] = useState(false);

  const {
    fetchStatus: trackedOrderFetchStatus,
    status: trackedOrderStatus,
    error: trackedOrderError,
  } = useTrackedOrder(trackOrderOptions);

  useEffect(() => {});

  useEffect(() => {
    if (trackedOrderStatus === 'success' && trackOrderOptions.email && trackOrderOptions.orderId) {
      localStorage.setItem('trackedOrderEmail', trackOrderOptions.email);
      router
        .push(routePaths.orderDetails.replace(':orderId', trackOrderOptions.orderId))
        .then(() => {
          setTrackedOrderLoading(false);
        });
    }
  }, [trackedOrderStatus, router, trackOrderOptions]);

  let formStarted = false;

  useEffect(() => {
    root.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (findOrderClicked) {
      if (trackedOrderStatus === 'error') {
        const errorMsg = getErrorCodeDescription(trackedOrderError);
        if (errorMsg) {
          trackOrderErrors(errorMsg);
          setFindOrderClicked(false);
          setErrMsg(errorMsg);
          setTrackedOrderLoading(false);
        }
      } else if (trackedOrderStatus === 'success') {
        trackSuccessfulTrackOrder();
        setFindOrderClicked(false);
        setErrMsg(getErrorCodeDescription(trackedOrderError) ?? '');
      }
    }
  }, [findOrderClicked, trackedOrderStatus, trackedOrderError]);

  useEffect(() => {
    const name = 'track-order';

    if (isUtagDefined) {
      const pageSpecificAnalyticsData = {
        pageName: getPageName(pageTypes.trackOrder, name),
        siteSection: siteSectionTrackOrder,
        pageType: pageTypes?.trackOrder,
        pageHierarchy: 'Home|Track Order',
        contentBlockNames: [],
        contentBlockTypes: [],
        seoPageType: getSeoPageType(pageTypes?.trackOrder, name),
      };
      pageViewCall(
        pageSpecificAnalyticsData,
        getAnalyticsRelativeComplementOf(pageSpecificAnalyticsData, undefined)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUtagDefined]);

  const handleFindOrder = (email: string, orderId: string) => {
    setFindOrderClicked(true);
    setTrackOrderOptions({ email, orderId });
    setTrackedOrderLoading(true);
    trackFormEndAction('trackOrderForm'); //Analytics
  };

  const renderTrackOrderForm = () => (
    <div className={styles.container}>
      <Title
        firstText={labels.lblTrack}
        secondText={labels.lblTrackMyOrder}
        stylesClass={styles.trackOrderTitleClass}
        firstTextClass={styles.trackOrderFirstText}
        secondTextClass={styles.trackOrderFirstText}
        swapStyle
        id="at_track_my_order"
        isSameTitleTag
        overRuleH1="h1"
      />
      {errorMessagge && <NotificationBlock message={errorMessagge} type="error" />}
      <p
        className={cx(
          azCommonStyles['az-body-1-regular'],
          azCommonStyles['az-margin-top-4xs'],
          azCommonStyles['az-margin-bottom-xs']
        )}
      >
        {labels.lblFindASingleOrderWithTheFormBelow}
      </p>
      <Formik
        validateOnBlur
        validateOnChange={true}
        initialValues={{
          email: '',
          orderNumber: '',
        }}
        onSubmit={(values) => {
          handleFindOrder(values.email, values.orderNumber);
        }}
      >
        {({ isValid }) => (
          <Form action="#" role="presentation" data-testid="trackOrderForm" noValidate>
            <Grid container>
              <Grid item xs={12} md={6}>
                <Field
                  required
                  type="email"
                  name="email"
                  id="emailFormik"
                  data-test="emailField"
                  component={FormikTextField}
                  autoComplete="off"
                  validate={(email: string) => {
                    // track when the user modifies the form
                    if (!formStarted) {
                      trackFormStartAction('trackOrderForm');
                      formStarted = true;
                    }

                    return validateEmailRequired(email) || validateEmail(email);
                  }}
                  label={labels.lblEmailAddress}
                  data-testid="track-order-email"
                  maxLength={40}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  required
                  inputMode="numeric"
                  type="tel"
                  id="orderNumber"
                  name="orderNumber"
                  autoComplete="on"
                  label={labels.lblOrderNumber}
                  component={FormikTextField}
                  data-testid="track-order-order-number"
                  validate={validateOrderNumberRequired}
                  maxLength={40}
                  normalize={(value: string) => value.replace(/[^\d]/g, '')}
                />
              </Grid>
            </Grid>
            <Grid container justify="flex-end" className={azCommonStyles['az-padding-top-4xs']}>
              <Grid item xs={12} md={3} lg={3}>
                <Button
                  name="findOrder"
                  data-testid="findOrder"
                  type="submit"
                  variant="contained"
                  fullWidth
                  placeholder={labels.lblFindOrder}
                  disabled={!isValid || trackedOrderFetchStatus !== 'idle' || trackedOrderLoading}
                >
                  {labels.lblFindOrder}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
      {trackedOrderLoading && <ProgressModal noScroll />}
    </div>
  );

  const parsePhoneNumber = () => {
    const regexp = new RegExp('\\??\\(?\\d+\\)?\\d*([\\s./-]?\\d{2,})+', 'g');
    const phoneNumber = labels.lblContactSupport.match(regexp);
    const filteredMessage =
      phoneNumber && phoneNumber.length > 0
        ? labels.lblContactSupport.replace(phoneNumber[0], '')
        : labels.lblContactSupport;
    return { filteredMessage, phoneNumber };
  };

  const { filteredMessage, phoneNumber } = parsePhoneNumber();

  const renderContactUs = () => (
    <Grid
      item
      xs={12}
      md={12}
      lg={8}
      className={cx(azCommonStyles['az-padding-left-xs'], azCommonStyles['az-padding-right-xs'])}
    >
      {!userAuthenticated && (
        <Grid container>
          <Grid item xs={12} md={6} lg={6}>
            <div className={cx(styles.registerText, azCommonStyles['az-body-2-regular'])}>
              {labels.lblRegisterToStart}{' '}
            </div>
          </Grid>
          <Grid item xs={12} md={6} lg={6}>
            <Grid container>
              <Grid item xs={6} md={6} lg={6}>
                <Button
                  name={labels.lblSignIn}
                  role="link"
                  variant="outlined"
                  fullWidth
                  placeholder={labels.lblSignIn}
                  onClick={() => {
                    localStorage.setItem('loginInteractionMethod', 'Track Order');
                    window.location.href = routePaths.signIn;
                  }}
                  data-testid="track-order-sign-in"
                >
                  {labels.lblSignIn}
                </Button>
              </Grid>
              <Grid item xs={6} md={6} lg={6}>
                <Button
                  name={labels.lblRegister}
                  role="link"
                  variant="outlined"
                  fullWidth
                  placeholder={labels.lblRegister}
                  onClick={() => {
                    localStorage.setItem('loginInteractionMethod', 'Track Order');
                    router.push(routePaths.create);
                  }}
                  data-testid="track-order-register"
                >
                  {labels.lblRegister}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
      <Grid
        container
        className={cx(
          azCommonStyles['az-body-2-regular'],
          azCommonStyles['az-padding-top-m'],
          styles.textCenter
        )}
      >
        <Grid item xs={12}>
          <span>{labels.lblProblem}</span>{' '}
          {locale !== countryCodes.mx ? (
            <Link data-testid="track-order-contact" className={styles.linktext} to="/contactus">
              {labels.lblContactSupport}
            </Link>
          ) : (
            <span data-testid="track-order-contact" className={styles.supportText}>
              {filteredMessage}
              <a href={`tel:+${phoneNumber}`}>{phoneNumber}</a>.
            </span>
          )}
        </Grid>
      </Grid>
    </Grid>
  );

  return (
    <div>
      <Head>
        <title>{labels.lblPageTitle}</title>
      </Head>
      <ContentContainer type="simple">
        <Grid
          container
          spacing={0}
          className={cx(
            azCommonStyles['az-padding-left-xs'],
            azCommonStyles['az-padding-right-xs']
          )}
        >
          <Grid item lg={2} />
          <Grid item xs={12} md={12} lg={8}>
            {renderTrackOrderForm()}
          </Grid>
        </Grid>

        <Grid
          container
          spacing={0}
          className={cx(
            azCommonStyles['az-padding-top-xxxl'],
            azCommonStyles['az-padding-bottom-xxxl'],
            styles.boxBg
          )}
        >
          <Grid item lg={2} />
          {renderContactUs()}
        </Grid>
      </ContentContainer>
    </div>
  );
};

export default TrackOrder;
